/**
 * Native device-pixel-ratio render control (#309, from the #265 finding).
 *
 * ROOT CAUSE. Cesium's `Viewer`/`CesiumWidget` defaults
 * `useBrowserRecommendedResolution = true`, which — per Cesium's own
 * `configurePixelRatio` — *ignores* `window.devicePixelRatio` and renders at
 * 1.0:
 *
 *   scene.pixelRatio = (useBrowserRecommendedResolution ? 1 : devicePixelRatio)
 *                      * resolutionScale
 *   canvas.width  = canvas.clientWidth  * scene.pixelRatio
 *   canvas.height = canvas.clientHeight * scene.pixelRatio
 *
 * So on a retina / hi-DPI display the WHOLE COP — basemap imagery, billboards,
 * labels, polylines — is rendered into a 1× backbuffer and upscaled by the
 * browser, looking soft, while FR24's DOM/canvas icons draw at native device
 * pixels. The #265 aircraft work proved the softness is NOT the icon artwork —
 * it is the globe at 1× DPR. Flipping Cesium to native DPR sharpens the entire
 * COP at once, at a QUADRATIC GPU fill-rate cost (a 2× display renders 4× the
 * fragments per frame), hence the measured perf gate this module exists to make
 * cheap to toggle.
 *
 * MECHANISM. We drive the effective ratio purely through `resolutionScale` and
 * pin `useBrowserRecommendedResolution = true` (so Cesium's own DPR multiply is
 * always 1). Then scene.pixelRatio === resolutionScale EXACTLY, regardless of
 * the real `devicePixelRatio`. That makes (a) the perf cap exact, (b) the QA
 * `?dpr=` override able to render at 2× even in a 1× headless context for
 * apples-to-apples before/after measurement, and (c) the ratio a single pure,
 * unit-testable number. The trade-off — Cesium no longer auto-tracks a
 * display-to-display DPR change — is covered by {@link watchDevicePixelRatio},
 * which re-applies the ratio when the window moves to a different-density
 * monitor.
 *
 * Dependency-free and resolved live (mirrors {@link ./demoMode}) so MapView can
 * read it at viewer init without pulling in React/prefs state.
 *
 * SHIP MODE (the founder gate). The operator-COP default is
 * {@link DEFAULT_NATIVE_DPR_ON}; demo mode forces it ON regardless (controlled
 * hardware, the demo must be crisp). The WALL is NOT forced (#309 follow-up) —
 * it follows the same opt-in as an operator (`?nativeDpr=1` /
 * `localStorage['tsr.nativeDpr']='1'`), so by default a wall renders at 1× like
 * the operator COP, for consistency. An operator (or wall) on weak hardware opts
 * THEIR machine out with `localStorage['tsr.nativeDpr'] = '0'` (or `?nativeDpr=0`),
 * which beats the demo force on that browser — so we never silently tank a
 * machine that has opted out.
 */

import { isDemoMode } from './demoMode';

/** Perf-safe ceiling on the live native-DPR ratio. A 3×/4× phone-class display
 *  would otherwise render 9×/16× the fragments for no perceptible operator gain;
 *  2× captures essentially all of the retina crispness at 4× the 1× cost. */
export const NATIVE_DPR_CAP = 2;

/** Absolute clamp bounds for any applied ratio (covers the QA `?dpr=` override,
 *  which may probe beyond the live cap to measure 3×/4× cost). */
export const MIN_RENDER_RATIO = 0.25;
export const MAX_RENDER_RATIO = 4;

/** localStorage key for the sticky per-browser operator opt-in/out. */
export const RENDER_RESOLUTION_STORAGE_KEY = 'tsr.nativeDpr';

/**
 * Operator-COP default when nothing else decides (no URL param, no stored
 * choice, not demo). Shipped FALSE per the #309 founder directive — "the
 * safest that still wins the demo; do not silently tank FPS on weaker hardware":
 * demo mode (controlled hardware) forces native DPR ON so the demo is always
 * crisp, while the operator COP — and now the wall too (#309 follow-up: opt-in,
 * not forced) — stays at the current 1× unless it opts in (`?nativeDpr=1` /
 * `localStorage['tsr.nativeDpr']='1'`) — so an unknown weak machine is never
 * silently pushed to 4× the fill rate.
 *
 * The #309 measurement supports flipping this to `true` (every operator gets the
 * crisp COP) once the founder approves the crispness/perf screenshot gate. On the
 * reference Apple M3 (headless Chrome, dpr=2, dense country scene of 186 entities)
 * the GPU render cost was measured by *amortized throughput* — N back-to-back
 * `viewer.render()` calls bracketed by one `gl.finish()`, since the occluded
 * headless window throttles rAF so wall-clock FPS can't be sampled there:
 *   1× → ~3.3 ms/frame (≈300 fps),  2× → ~11.8 ms/frame (≈85 fps),  3× → ~20.7 ms
 *   (≈48 fps). So native DPR (2×) costs ~3.55× the 1× *rasterization/fill* time
 * but still clears the 60fps frame budget with headroom (it is vsync-capped to 60
 * in normal use); 3× falls under 60 → why {@link NATIVE_DPR_CAP} = 2. Absolute
 * FPS on a weaker GPU will differ; the *ratio* (≈4× fill per doubling) is the
 * transferable cost. The flip is this one constant — no other code changes.
 */
export const DEFAULT_NATIVE_DPR_ON = false;

/** Test seam — pin the enabled flag, or `null` to restore live resolution. */
let enabledOverride: boolean | null = null;

/** Test seam for {@link nativeDprEnabled}. */
export function __setNativeDprOverride(value: boolean | null): void {
  enabledOverride = value;
}

function truthyParam(value: string | null): boolean | null {
  if (value == null || value === '') return null;
  if (value === '0' || value === 'false' || value === 'off') return false;
  return true;
}

/**
 * Clamp an arbitrary requested ratio into the sane render range. Non-finite or
 * non-positive → 1.0 (the safe 1× baseline).
 */
export function clampRenderRatio(ratio: number): number {
  if (!Number.isFinite(ratio) || ratio <= 0) return 1;
  return Math.min(Math.max(ratio, MIN_RENDER_RATIO), MAX_RENDER_RATIO);
}

/**
 * The live native-DPR ratio for a given display density: the device pixel ratio
 * clamped to [1, cap]. Below 1 (sub-CSS density) we never DOWN-sample — 1× is
 * the floor for the live path; the QA override is the only way below 1.
 */
export function nativeDprRatio(devicePixelRatio: number, cap: number = NATIVE_DPR_CAP): number {
  if (!Number.isFinite(devicePixelRatio) || devicePixelRatio < 1) return 1;
  return Math.min(devicePixelRatio, cap);
}

/**
 * Is native-DPR rendering enabled for this load? Resolved live, in precedence
 * order (most explicit first):
 *   1. test override.
 *   2. `?nativeDpr=` URL param (per-load intent — the wall/operator opt-in, and
 *      the escape hatch from the demo force).
 *   3. `localStorage['tsr.nativeDpr']` (operator's sticky per-machine choice —
 *      beats the demo force so an opted-out weak machine stays 1×).
 *   4. demo mode → ON (controlled hardware, demo must be crisp). The WALL is NOT
 *      forced here (#309 follow-up) — it opts in like an operator via step 2/3,
 *      so by default it falls through to step 5 (1×) like the operator COP.
 *   5. {@link DEFAULT_NATIVE_DPR_ON}.
 * Returns the default outside a browser context.
 */
export function nativeDprEnabled(): boolean {
  if (enabledOverride !== null) return enabledOverride;
  try {
    const fromQuery = truthyParam(new URLSearchParams(window.location.search).get('nativeDpr'));
    if (fromQuery !== null) return fromQuery;
    const stored = window.localStorage.getItem(RENDER_RESOLUTION_STORAGE_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
    if (isDemoMode()) return true;
    return DEFAULT_NATIVE_DPR_ON;
  } catch {
    return DEFAULT_NATIVE_DPR_ON;
  }
}

/**
 * QA-only forced ratio: render at an explicit resolutionScale regardless of the
 * real display, via `?dpr=` or `window.__tsrForceRenderRatio`. This is how #309
 * measures 1× vs 2× (and 3×/4× cost) in a single headless context where
 * `devicePixelRatio` is 1. Returns null when no override is set (the normal
 * production path).
 */
export function forcedRenderRatio(): number | null {
  try {
    const fromWindow = (window as unknown as { __tsrForceRenderRatio?: unknown }).__tsrForceRenderRatio;
    if (typeof fromWindow === 'number' && Number.isFinite(fromWindow) && fromWindow > 0) {
      return clampRenderRatio(fromWindow);
    }
    const raw = new URLSearchParams(window.location.search).get('dpr');
    if (raw != null && raw !== '') {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return clampRenderRatio(n);
    }
    return null;
  } catch {
    return null;
  }
}

export interface ResolvedRenderResolution {
  /** The resolutionScale to apply (=== scene.pixelRatio with the flag pinned). */
  ratio: number;
  /** Why this ratio — for the QA readout / report. */
  source: 'forced' | 'native-dpr' | 'disabled';
  /** The display density this was resolved against. */
  devicePixelRatio: number;
}

/**
 * Resolve the effective render ratio for the current load. Pass an explicit
 * `devicePixelRatio` in tests; defaults to `window.devicePixelRatio` (1 outside
 * a browser).
 */
export function resolveRenderResolution(devicePixelRatio?: number): ResolvedRenderResolution {
  const dpr =
    devicePixelRatio ??
    (typeof window !== 'undefined' && Number.isFinite(window.devicePixelRatio)
      ? window.devicePixelRatio
      : 1);
  const forced = forcedRenderRatio();
  if (forced != null) return { ratio: forced, source: 'forced', devicePixelRatio: dpr };
  if (!nativeDprEnabled()) return { ratio: 1, source: 'disabled', devicePixelRatio: dpr };
  return { ratio: nativeDprRatio(dpr), source: 'native-dpr', devicePixelRatio: dpr };
}

/** The slice of the Cesium Viewer this module writes — structural so it can be
 *  unit-tested with a lightweight fake (cesiumRenderGuard / occlusionPump
 *  pattern). */
export interface ResolutionViewer {
  useBrowserRecommendedResolution: boolean;
  resolutionScale: number;
}

/**
 * Apply a render ratio to a viewer. Pins `useBrowserRecommendedResolution` true
 * and drives the ratio through `resolutionScale` (see module docblock), so the
 * effective scene.pixelRatio is exactly the clamped ratio. Returns the ratio
 * actually applied.
 */
export function applyRenderRatio(viewer: ResolutionViewer, ratio: number): number {
  const applied = clampRenderRatio(ratio);
  viewer.useBrowserRecommendedResolution = true;
  viewer.resolutionScale = applied;
  return applied;
}

/**
 * Re-run `onChange` whenever the window's `devicePixelRatio` changes (e.g. the
 * window is dragged to a monitor of different density). Needed because pinning
 * `useBrowserRecommendedResolution = true` means Cesium's own resize loop no
 * longer reacts to a DPR change. Returns a disposer. No-op outside a browser /
 * without matchMedia.
 */
export function watchDevicePixelRatio(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }
  let mql: MediaQueryList | null = null;
  const handler = (): void => {
    if (mql) mql.removeEventListener('change', handler);
    onChange();
    arm(); // re-arm against the new density
  };
  function arm(): void {
    try {
      mql = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
      mql.addEventListener('change', handler);
    } catch {
      mql = null;
    }
  }
  arm();
  return () => {
    if (mql) mql.removeEventListener('change', handler);
  };
}
